import { DOCUMENT, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    signal,
    Signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { BLOCKS, Heading2 } from '@contentful/rich-text-types';
import { EnvironmentService } from '@core/services/environment.service';
import { Heading2Component } from '@features/contentful-dynamic/rich-text/components/heading2/heading2.component';
import { KnowledgeArticle } from '@generated/graphql';

@Component({
    selector: 'app-knowledge-article-toc',
    templateUrl: './knowledge-article-toc.component.html',
    styles: ``,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [RouterLink, NgClass],
})
export class KnowledgeArticleTocComponent {
    private document = inject(DOCUMENT);
    private environmentService = inject(EnvironmentService);
    activeFragment = signal(null);

    article = input.required<KnowledgeArticle>();
    tableOfContents: Signal<{ id: string; text: string }[]> = computed(() => {
        const body = this.article().body.json.content || [];
        return body
            .filter(node => node.nodeType === BLOCKS.HEADING_2)
            .map((node: Heading2) => ({
                text: Heading2Component.extractText(node),
                id: Heading2Component.getId(node),
            }))
            .filter(data => !!data.text);
    });

    observers = computed(() => {
        if (this.environmentService.isServer()) {
            return [];
        }
        const tableOfContents = this.tableOfContents();
        return tableOfContents.map(node => {
            const options = {
                root: null,
                rootMargin: '0px',
                threshold: 1.0,
            };
            return {
                observer: new IntersectionObserver((entries, observer) => {
                    const isVisible = entries
                        .map(value => value.isIntersecting)
                        .reduce(
                            (previousValue, currentValue) =>
                                previousValue && currentValue,
                            true
                        );
                    if (isVisible) {
                        this.activeFragment.set(node.id);
                    }
                }, options),
                node: node,
            };
        });
    });

    constructor() {
        effect(onCleanup => {
            const observers = this.observers();

            for (const { observer, node } of observers) {
                const element = this.document.getElementById(node.id);
                if (element) {
                    observer.observe(element);
                }
            }
            onCleanup(() => {
                for (const { observer } of observers) {
                    observer.disconnect();
                }
            });
        });
    }
}
